// Printable report window: the browser's print dialog, where "Save as PDF" gives the PDF. Used by the pages that offer
// "Print / PDF" next to their Excel export.

export const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const fmt = (value, column) => {
  if (value === null || value === undefined || value === "") return "-";
  if (column.format === "money") return Number(value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (column.format === "qty") return Number(value).toLocaleString("en-IN", { maximumFractionDigits: 3 });
  if (column.format === "signedMoney")
    return `${Number(value) > 0 ? "+" : ""}${Number(value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return String(value);
};

const numeric = (column) => column.format === "money" || column.format === "qty" || column.format === "signedMoney";

/** One table as HTML: columns [{ key, label, format?, render? }], rows, and a totals row over the numeric columns when asked. */
export const tableHtml = (columns, rows, { totals = false, totalLabel = "Total" } = {}) => {
  const head = columns.map((c) => `<th>${escapeHtml(c.label)}</th>`).join("");
  const body = rows.length
    ? rows
        .map(
          (row) =>
            `<tr>${columns
              .map((c) => `<td class="${numeric(c) ? "r" : ""}">${escapeHtml(c.render ? c.render(row) : fmt(row[c.key], c))}</td>`)
              .join("")}</tr>`
        )
        .join("")
    : `<tr><td colspan="${columns.length}" class="c">No records</td></tr>`;
  const foot =
    totals && rows.length
      ? `<tfoot><tr>${columns
          .map((c, i) =>
            i === 0
              ? `<td><b>${escapeHtml(totalLabel)}</b></td>`
              : numeric(c) && c.total !== false
              ? `<td class="r"><b>${escapeHtml(fmt(rows.reduce((s, r) => s + (Number(r[c.key]) || 0), 0), c))}</b></td>`
              : "<td></td>"
          )
          .join("")}</tr></tfoot>`
      : "";
  return `<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody>${foot}</table>`;
};

/** Opens the print window with a heading and the given body HTML (tables, sections). */
export const printHtml = (title, subtitle, bodyHtml, { landscape = true } = {}) => {
  const win = window.open("", "_blank");
  if (!win) {
    window.alert("Allow pop-ups for this site to print or save the PDF.");
    return;
  }
  win.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title>
<style>
  @page { size: A4 ${landscape ? "landscape" : "portrait"}; margin: 10mm; }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 11px; color: #111; }
  h1 { font-size: 16px; text-align: center; margin: 0 0 4px; }
  h2 { font-size: 13px; margin: 14px 0 6px; }
  .sub { text-align: center; color: #444; margin-bottom: 10px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 10px; }
  th, td { border: 1px solid #999; padding: 3px 5px; vertical-align: top; }
  th { background: #e6e6e6; }
  td.r { text-align: right; white-space: nowrap; }
  td.c { text-align: center; }
  .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
  .muted { color: #555; }
</style></head><body>
<h1>${escapeHtml(title)}</h1>${subtitle ? `<div class="sub">${escapeHtml(subtitle)}</div>` : ""}
${bodyHtml}
<script>window.onload = function () { setTimeout(function () { window.print(); }, 200); };</script>
</body></html>`);
  win.document.close();
};

/** A single-table report in the print window. */
export const printTable = (title, subtitle, columns, rows, options = {}) =>
  printHtml(title, subtitle, tableHtml(columns, rows, options), options);
